<template>
    <AuthLayout>
        <div
            class="mt-8 w-full overflow-hidden bg-white p-6 shadow-xl sm:mt-14 sm:max-w-md sm:rounded-lg"
        >
            <div class="mt-[2px] border-b border-gray-200 pb-4">
                <h1 class="text-center text-xl font-bold text-gray-800">
                    {{ t("vue.two_factor_authentication") }}
                </h1>

                <div class="mt-3 text-center text-sm text-gray-500">
                    <template v-if="!recovery">
                        {{ t("vue.two_factor_authentication_enabled_message") }}
                    </template>

                    <template v-else>
                        {{ t("vue.enter_emergency_recovery_code") }}
                    </template>
                </div>
            </div>

            <form @submit.prevent="submit">
                <div class="mt-7 flex flex-col items-center">
                    <OtpInput
                        v-if="!recovery"
                        input-classes="mt-2 rounded-md text-center border border-gray-300 mx-1 sm:mx-2 focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 focus:outline-hidden transition duration-200 ease-in-out"
                        separator=""
                        :num-inputs="6"
                        :should-auto-focus="true"
                        :is-input-num="true"
                        :error="getErrorCode('code')"
                        @on-change="handleOnChange"
                    />

                    <TextInput
                        v-if="recovery"
                        v-model="v$.formRecoveryCode.recovery_code.$model"
                        required
                        suppress-required-icon
                        :label="$t('vue.recovery_code')"
                        :placeholder="$t('vue.enter_your_recovery_code')"
                        class="mt-0 w-full"
                        :error="getErrorRecoveryCode('recovery_code')"
                    />

                    <MegaButton class="mt-12" :disabled="loading" type="submit"
                        >{{ t("vue.verify") }}
                    </MegaButton>

                    <button
                        class="mb-2 mt-6 text-xs font-medium text-custom-blue-500 hover:text-custom-blue-600"
                        @click.prevent="toggleRecovery"
                    >
                        <template v-if="!recovery">{{
                            t("vue.enter_a_recovery_code")
                        }}</template>

                        <template v-else>{{
                            t("vue.use_regular_authentication_code_instead")
                        }}</template>
                    </button>
                </div>
            </form>
        </div>
    </AuthLayout>
</template>

<script lang="ts" setup>
import { reactive, ref } from "vue";
import route from "ziggy-js";
import useVuelidate from "@vuelidate/core";
import { required, minLength } from "@vuelidate/validators";
import { router } from "@inertiajs/vue3";
import type IInputErrors from "@/types/Atomic/IInputErrors";
import { useTranslations } from "@/composables/useTranslations";

const recovery = ref(false);

// Get access to translations
const { t } = useTranslations();

const state = reactive({
    formCode: {
        code: "",
    },
    formRecoveryCode: {
        recovery_code: "",
    },
});

const rules = {
    formCode: {
        code: { required, minLength: minLength(6) },
    },
    formRecoveryCode: {
        recovery_code: { required },
    },
};

const loading = ref(false);
const errors = ref<IInputErrors>({});
const v$ = useVuelidate(rules, state);

const toggleRecovery = async () => {
    recovery.value = !recovery.value;

    if (recovery.value) {
        v$.value.formCode.$reset();
        state.formCode.code = "";
    } else {
        v$.value.formRecoveryCode.$reset();
        state.formRecoveryCode.recovery_code = "";
    }
};

const handleOnChange = (value: number) => {
    state.formCode.code = value.toString();
};

const submit = async () => {
    if (recovery.value) {
        submitRecoveryCode();
    } else submitCode();
};

const submitCode = () => {
    v$.value.formCode.$touch();
    if (v$.value.formCode.$error) {
        () => undefined;
    } else {
        loading.value = true;
        router.post(route("two-factor.login"), state.formCode, {
            preserveScroll: true,
            onError: (err) => {
                errors.value = err;
            },
            onFinish: () => {
                loading.value = false;
            },
        });
    }
};

const submitRecoveryCode = () => {
    v$.value.formRecoveryCode.$touch();
    if (v$.value.formRecoveryCode.$error) {
        () => undefined;
    } else {
        loading.value = true;
        router.post(route("two-factor.login"), state.formRecoveryCode, {
            preserveScroll: true,
            onError: (err) => {
                errors.value = err;
            },
            onFinish: () => {
                loading.value = false;
            },
        });
    }
};

const getErrorRecoveryCode = (field: string) => {
    return (
        v$.value.formRecoveryCode[field]?.$errors[0]?.$message ||
        errors.value?.[field] ||
        ""
    );
};

const getErrorCode = (field: string) => {
    return (
        v$.value.formCode[field]?.$errors[0]?.$message ||
        errors.value?.[field] ||
        ""
    );
};
</script>
